import wccEntityAdapterBuilder from 'builders/entityAdapterBuilder';
import { wccModules } from 'enums/wccModules';
import { AttachmentsHelpers } from 'helpers/attachments';
import { ImageHelpers } from 'helpers/images';
import { inject, injectable } from 'inversify';
import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import { EffectsContainer } from 'mixins/withEffects';
import WCCImage from 'models/attachments/image';
import UserFile from 'models/userFile';
import './image.html';
import './image.scss';

export interface WCCImageVMConfig {
    image: SubscribableOrNullableValue<WCCImage>

    src: SubscribableOrNullableValue<string>
    width: SubscribableOrNullableValue<number>
    height: SubscribableOrNullableValue<number>

    loaded: SubscribableOrNullableValue<boolean>
    crossorigin: SubscribableOrNullableValue<string>
    cover: SubscribableOrNullableValue<boolean | number>
}

export interface WCCImageVMData {
    loaded: SubscribableOrNullableValue<boolean>
    crossorigin: SubscribableOrNullableValue<string>
}

@injectable()
export default class WCCImageVM {
    icon = 'fal fa-image';

    isLoaded = ko.observable(false);
    isFailed = ko.observable(false);

    file: Subscribable<UserFile | undefined>
    src: Subscribable<string | undefined>
    crossorigin: Subscribable<string | undefined>

    width: Subscribable<number>
    height: Subscribable<number>
    ratio: Subscribable<number>    

    isPlaceholderShown: Subscribable<boolean>
    isImageShown: Subscribable<boolean>
    isCoverShown: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) configOrImage: WCCImageVMConfig | WCCImage,
        @inject(wccModules.componentData) data: WCCImageVMData,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        let imageRaw: SubscribableOrNullableValue<WCCImage>
        let loadedRaw: SubscribableOrNullableValue<boolean>
        let crossoriginRaw: SubscribableOrNullableValue<string>
        let isCoverModeOrCoverRatioRaw: SubscribableOrNullableValue<boolean | number>

        if (configOrImage instanceof WCCImage) {
            imageRaw = configOrImage;
            loadedRaw = data.loaded;
            crossoriginRaw = data.crossorigin;
        } else {
            if (configOrImage.src != undefined) {
                const src = ko.flattenComputed(configOrImage.src);
                const width = ko.flattenComputed(configOrImage.width);
                const height = ko.flattenComputed(configOrImage.height);

                const constructedImage = new WCCImage();

                wccEntityAdapterBuilder.createBindings(constructedImage, {
                    link: src,
                    width: width,
                    height: height
                });

                imageRaw = constructedImage;
            } else {
                imageRaw = configOrImage.image;
            }

            loadedRaw = configOrImage.loaded;
            crossoriginRaw = configOrImage.crossorigin;
            isCoverModeOrCoverRatioRaw = configOrImage.cover;
        }

        const image = ko.flattenComputed(imageRaw);

        this.crossorigin = ko.flattenComputed(crossoriginRaw);

        const isCoverModeOrCoverRatio = ko.flattenComputed(isCoverModeOrCoverRatioRaw, false);
        const isCoverMode = isCoverModeOrCoverRatio.mapSingle(isCoverModeOrCoverRatio => _.isNumber(isCoverModeOrCoverRatio) ? true : isCoverModeOrCoverRatio);

        this.file = image.pluck('lqFile');
        this.src = this.file.pluck('link');

        this.width = image.pluck('width', 0);
        this.height = image.pluck('height', 0);

        this.ratio = ko.strictComputed((width, height, isCoverModeOrCoverRatio) => {
            return AttachmentsHelpers.getRatio(width, height, isCoverModeOrCoverRatio);
        }, [this.width, this.height, isCoverModeOrCoverRatio]);

        const isHEIC = this.src.mapSingle(src => {
            if (src != undefined)
                return src.toLowerCase().indexOf('.heic') !== -1;

            return false;
        });

        const isExists = this.src.isNotNull();
        const isValid = ko.pureComputed(() => isExists() && !isHEIC());

        this.isPlaceholderShown = ko.pureComputed(() => !isValid() || !this.isLoaded() || this.isFailed());
        this.isImageShown = ko.pureComputed(() => isValid() && !isCoverMode());
        this.isCoverShown = ko.pureComputed(() => isValid() && isCoverMode());

        const imageExists = ko.fromPromise(src => {
            if (src != undefined)
                return ImageHelpers.checkIfImageExists(src);
        }, [this.src], <boolean>false);

        effects.register(imageExists => {
            this.isLoaded(imageExists);
            this.isFailed(!imageExists);
        }, [imageExists]);

        effects.register(isLoaded => {
            if (ko.isWritableObservable(loadedRaw))
                loadedRaw(isLoaded);
        }, [this.isLoaded]);

        ko.applyBindingsToNode(info.element, {
            'visible': this.src.isNotNull(),

            'css': {
                'wcc-image': true
            }
        });
    }
}